import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL ;

/**
 * Servicio API centralizado
 * Maneja todas las llamadas HTTP al backend
 */
class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      }
    });

    // Interceptor para agregar token de autenticación
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('app_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Interceptor para manejar errores globalmente
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Sesión expirada, redirigir a login
          localStorage.removeItem('app_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // ========================================
  // WALLETS
  // ========================================

  async getWalletsSummary(userId = null) {
    const params = userId ? { userId } : {};
    const res = await this.client.get('/wallets/summary', { params });
    return res.data.data;
  }

  async syncWallets(userId = null) {
    const res = await this.client.post('/wallets/sync', { userId });
    return res.data;
  }

  // ========================================
  // PERFORMANCE
  // ========================================

  async getPerformanceMetrics(userId = null, period = '30d') {
    const params = { period };
    if (userId) params.userId = userId;
    const res = await this.client.get('/performance/summary', { params });
    return res.data.data;
  }

  // ========================================
  // ACCOUNT MOVEMENTS
  // ========================================

  async getAccountStatement(userId = null, filters = {}) {
    const params = { ...filters };
    if (userId) params.userId = userId;
    const res = await this.client.get('/account/statement', { params });
    return res.data.data;
  }

  async getDeposits(userId = null, filters = {}) {
    const params = { ...filters };
    if (userId) params.userId = userId;
    const res = await this.client.get('/account/deposits', { params });
    return res.data.data;
  }

  async getWithdrawals(userId = null, filters = {}) {
    const params = { ...filters };
    if (userId) params.userId = userId;
    const res = await this.client.get('/account/withdrawals', { params });
    return res.data.data;
  }

  async syncMovements(userId = null, daysBack = 30) {
    const res = await this.client.post('/account/sync', { userId, daysBack });
    return res.data;
  }

  // ========================================
  // GRID BOT
  // ========================================

  async getGridDashboard(userId = null) {
    const params = userId ? { userId } : {};
    const res = await this.client.get('/grid/dashboard', { params });
    return res.data.data;
  }

  async getGridPositions(userId = null) {
    const params = userId ? { userId } : {};
    const res = await this.client.get('/grid/positions', { params });
    return res.data.data;
  }

  async getGridTrades(userId = null, filters = {}) {
    const params = { ...filters };
    if (userId) params.userId = userId;
    const res = await this.client.get('/grid/trades', { params });
    return res.data.data;
  }

  async getGridConfig(userId = null) {
    const params = userId ? { userId } : {};
    const res = await this.client.get('/grid/config', { params });
    return res.data.data;
  }

  async updateGridConfig(userId, config) {
    const res = await this.client.put('/grid/config', { userId, ...config });
    return res.data;
  }

  async startGridBot(userId = null) {
    const res = await this.client.post('/grid/bot/start', { userId });
    return res.data;
  }

  async stopGridBot(userId = null) {
    const res = await this.client.post('/grid/bot/stop', { userId });
    return res.data;
  }

  // ========================================
  // SNIPER BOT (Solana)
  // ========================================

  async getSniperDashboard(userId = null) {
    const params = userId ? { userId } : {};
    const res = await this.client.get('/sniper/dashboard', { params });
    return res.data.data;
  }

  async getSniperPositions(userId = null) {
    const params = userId ? { userId } : {};
    const res = await this.client.get('/sniper/positions', { params });
    return res.data.data;
  }

  async getSniperActivity(userId = null, filters = {}) {
    const params = { ...filters };
    if (userId) params.userId = userId;
    const res = await this.client.get('/sniper/activity', { params });
    return res.data.data;
  }

  async getSniperConfig(userId = null) {
    const params = userId ? { userId } : {};
    const res = await this.client.get('/sniper/config', { params });
    return res.data.data;
  }

  async updateSniperConfig(userId, config) {
    const res = await this.client.put('/sniper/config', { userId, ...config });
    return res.data;
  }

  // ========================================
  // FUTURES BOT
  // ========================================

  async getFuturesDashboard(userId = null) {
    const params = userId ? { userId } : {};
    // Combine positions and stats into a dashboard response
    const [positions, stats] = await Promise.all([
      this.client.get('/futures/positions', { params }).then(res => res.data.data || []),
      this.client.get('/futures/stats', { params }).then(res => res.data.data || {}).catch(() => ({}))
    ]);
    
    return {
      positions,
      performance: stats
    };
  }

  async getFuturesPositions(userId = null) {
    const params = userId ? { userId } : {};
    const res = await this.client.get('/futures/positions', { params });
    return res.data.data;
  }

  async getFuturesHistory(userId = null, filters = {}) {
    const params = { ...filters };
    if (userId) params.userId = userId;
    const res = await this.client.get('/futures/history', { params });
    return res.data.data;
  }

  async getFuturesStats(userId = null) {
    const params = userId ? { userId } : {};
    const res = await this.client.get('/futures/stats', { params });
    return res.data.data;
  }

  async getFuturesConfig(userId = null) {
    const params = userId ? { userId } : {};
    const res = await this.client.get('/bot/config/futures', { params });
    return res.data.data;
  }

  async updateFuturesConfig(userId, config) {
    const res = await this.client.post('/bot/config/futures', { userId, ...config });
    return res.data;
  }

  // ========================================
  // USERS (Admin only)
  // ========================================

  async getAllUsers() {
    const res = await this.client.get('/users');
    return res.data || [];
  }

  async getUser(userId) {
    const res = await this.client.get(`/usuarios/${userId}`);
    return res.data.data;
  }

  async createUser(userData) {
    const res = await this.client.post('/usuarios', userData);
    return res.data;
  }

  async updateUser(userId, userData) {
    const res = await this.client.put(`/usuarios/${userId}`, userData);
    return res.data;
  }

  async deleteUser(userId) {
    const res = await this.client.delete(`/usuarios/${userId}`);
    return res.data;
  }

  // ========================================
  // BOT CONFIG (Admin only)
  // ========================================

  async getBotConfig(userId, botType) {
    const res = await this.client.get(`/bot/config/${userId}/${botType}`);
    return res.data.data;
  }

  async updateBotConfig(userId, botType, config) {
    const res = await this.client.put(`/bot/config/${userId}/${botType}`, config);
    return res.data;
  }

  // ========================================
  // AUTH
  // ========================================

  async login(credentials) {
    const res = await this.client.post('/auth/login', credentials);
    return res.data;
  }

  async logout() {
    const res = await this.client.post('/auth/logout');
    return res.data;
  }

  async changePassword(oldPassword, newPassword) {
    const res = await this.client.post('/auth/change-password', {
      oldPassword,
      newPassword
    });
    return res.data;
  }
}

// Exportar instancia única (singleton)
const api = new ApiService();
export default api;
